import { useState } from "react";
import { 
  BookOpen, 
  Check, 
  TrendingUp,
  Calculator,
  FileText,
  BarChart3,
  ExternalLink,
  ArrowRight
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const packages = [
  {
    name: "Starter",
    description: "For solo entrepreneurs and small freelancers",
    features: [
      "Bank account reconciliation (1 account)",
      "Credit card reconciliation (1 card)",
      "Monthly P&L statement",
      "Expense categorization",
      "Email support"
    ],
    ideal: "New businesses with simple transactions"
  },
  {
    name: "Growth",
    description: "For growing businesses with multiple accounts",
    popular: true,
    features: [
      "Bank reconciliation (up to 3 accounts)",
      "Credit card reconciliation (up to 2 cards)",
      "Monthly P&L and Balance Sheet",
      "Accounts receivable tracking",
      "Sales tax preparation support",
      "Priority email & phone support"
    ],
    ideal: "Established small businesses with regular revenue"
  },
  {
    name: "Pro",
    description: "Full-service for complex business needs",
    features: [
      "Unlimited account reconciliation",
      "Full financial statement package",
      "Accounts payable & receivable",
      "Inventory tracking support",
      "Payroll reconciliation",
      "Dedicated bookkeeper",
      "Quarterly review calls"
    ],
    ideal: "Growing businesses with inventory, employees, or complex needs"
  }
];

const services = [
  {
    icon: Calculator,
    title: "Bank & Credit Card Reconciliation",
    description: "Monthly matching of all transactions to ensure accuracy and catch discrepancies."
  },
  {
    icon: FileText,
    title: "Monthly Financial Statements",
    description: "Clear P&L and balance sheet reports delivered by the 15th of each month."
  },
  {
    icon: TrendingUp,
    title: "Cleanup & Catch-Up",
    description: "Behind on your books? We'll get you caught up and organized quickly."
  },
  {
    icon: BarChart3,
    title: "Sales Tax Support",
    description: "Preparation and filing support for sales tax where applicable."
  }
];

export default function Bookkeeping() {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    business_name: "",
    package_interest: "",
    message: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/bookkeeping-inquiries`, formData);
      toast.success("Thank you! We'll be in touch within 1-2 business days.");
      setFormData({
        name: "",
        email: "",
        phone: "",
        business_name: "",
        package_interest: "",
        message: ""
      });
    } catch (error) {
      toast.error("Something went wrong. Please try again or contact us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="bookkeeping-page">
      {/* Hero */}
      <section className="bg-navy text-white noise-overlay py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="font-mono text-bronze text-sm mb-4 tracking-wider uppercase">
              Bookkeeping & Accounting
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
              Keep Your Books Clean, Year-Round
            </h1>
            <p className="font-body text-lg md:text-xl text-gray-300 mb-8">
              Professional monthly bookkeeping services that give you clarity, save you time, and keep you tax-ready.
            </p>
            <Button
              asChild
              className="bg-bronze hover:bg-bronze/90 text-navy rounded-full px-8 py-6 text-lg font-medium"
              data-testid="bookkeeping-cta-btn"
            >
              <a href="#inquiry-form">
                Request a Quote
                <ArrowRight className="ml-2 w-5 h-5" />
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="bg-bone py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="font-mono text-bronze text-sm mb-3 tracking-wider uppercase">What We Offer</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-navy">
              Comprehensive Bookkeeping Services
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <Card 
                key={service.title} 
                className="bg-white border-0 shadow-sm hover-lift"
                data-testid={`bookkeeping-service-${index}`}
              >
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-xl bg-bone flex items-center justify-center mb-4">
                    <service.icon className="w-6 h-6 text-bronze" />
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-navy mb-2">
                    {service.title}
                  </h3>
                  <p className="font-body text-gray-600 text-sm">
                    {service.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Packages */}
      <section className="bg-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="font-mono text-bronze text-sm mb-3 tracking-wider uppercase">Packages</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-navy mb-4">
              Monthly Bookkeeping Packages
            </h2>
            <p className="font-body text-gray-600 max-w-2xl mx-auto">
              Choose the package that fits your business. All packages include QuickBooks/Xero support.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {packages.map((pkg, index) => (
              <Card 
                key={pkg.name}
                className={`relative border-2 ${pkg.popular ? 'border-bronze' : 'border-gray-100'} hover-lift`}
                data-testid={`package-${pkg.name.toLowerCase()}`}
              >
                {pkg.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-bronze text-navy text-xs font-bold px-4 py-1 rounded-full">
                      MOST POPULAR
                    </span>
                  </div>
                )}
                <CardHeader className="text-center pb-4">
                  <CardTitle className="font-heading text-2xl text-navy">
                    {pkg.name}
                  </CardTitle>
                  <p className="font-body text-gray-600 text-sm mt-2">
                    {pkg.description}
                  </p>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3 mb-6">
                    {pkg.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-2 font-body text-sm text-gray-700">
                        <Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <div className="pt-4 border-t border-gray-100">
                    <p className="font-body text-xs text-gray-500 mb-4">
                      <strong>Ideal for:</strong> {pkg.ideal}
                    </p>
                    <Button
                      asChild
                      className={`w-full rounded-full font-medium ${
                        pkg.popular 
                          ? 'bg-bronze hover:bg-bronze/90 text-navy' 
                          : 'bg-navy hover:bg-gray-800 text-white'
                      }`}
                    >
                      <a href="#inquiry-form">
                        Request Quote
                      </a>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Inquiry Form */}
      <section id="inquiry-form" className="bg-bone py-20 md:py-28 scroll-mt-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="font-mono text-bronze text-sm mb-3 tracking-wider uppercase">Get Started</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-navy mb-4">
              Bookkeeping Inquiry
            </h2>
            <p className="font-body text-gray-600">
              Tell us about your business and we'll get back to you with a customized quote.
            </p>
          </div>

          <Card className="bg-white border-0 shadow-lg">
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="bookkeeping-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="font-body">Name *</Label>
                    <Input
                      id="name"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="h-12 rounded-lg"
                      data-testid="bookkeeping-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="font-body">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="h-12 rounded-lg"
                      data-testid="bookkeeping-email-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="font-body">Phone</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      className="h-12 rounded-lg"
                      data-testid="bookkeeping-phone-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="business" className="font-body">Business Name</Label>
                    <Input
                      id="business"
                      value={formData.business_name}
                      onChange={(e) => setFormData({...formData, business_name: e.target.value})}
                      className="h-12 rounded-lg"
                      data-testid="bookkeeping-business-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="package" className="font-body">Package Interest *</Label>
                  <Select
                    value={formData.package_interest}
                    onValueChange={(value) => setFormData({...formData, package_interest: value})}
                    required
                  >
                    <SelectTrigger className="h-12 rounded-lg" data-testid="bookkeeping-package-select">
                      <SelectValue placeholder="Select a package" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="starter">Starter</SelectItem>
                      <SelectItem value="growth">Growth</SelectItem>
                      <SelectItem value="pro">Pro</SelectItem>
                      <SelectItem value="not-sure">Not Sure - Need Guidance</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message" className="font-body">Tell us about your business</Label>
                  <Textarea
                    id="message"
                    rows={4}
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    placeholder="What industry are you in? How many transactions per month? Any specific needs?"
                    className="rounded-lg"
                    data-testid="bookkeeping-message-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-navy hover:bg-gray-800 text-white rounded-full py-6 text-lg font-medium"
                  data-testid="bookkeeping-submit-btn"
                >
                  {loading ? "Sending..." : "Request Quote"}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-navy text-white noise-overlay py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold mb-4">
            Prefer to Discuss First?
          </h2>
          <p className="font-body text-gray-300 mb-8">
            Schedule a free consultation to talk through your bookkeeping needs.
          </p>
          <Button
            asChild
            className="bg-bronze hover:bg-bronze/90 text-navy rounded-full px-8 py-6 text-lg font-medium"
            data-testid="bookkeeping-cta-book"
          >
            <a href="#book" onClick={(e) => { e.preventDefault(); window.dispatchEvent(new CustomEvent("open-booking")); }}>
              Book Free Consultation
              <ExternalLink className="ml-2 w-5 h-5" />
            </a>
          </Button>
        </div>
      </section>
    </div>
  );
}
